import React, { useState, useEffect } from "react";
import { Create, useForm } from "@refinedev/antd";
import { useNavigation } from "@refinedev/core";
import { Form, Input, Select, Button, Space, message, Spin } from "antd";
import { CREATE_AND_INVITE_USER_MUTATION, GET_AUTH_TEAMS_QUERY, GET_APP_TEAMS_QUERY } from "./queries";
import { User, AuthTeam } from "./interface";
import { gqlClient } from "../../helpers/gqlProvider";

const { Option } = Select;

interface AppTeam {
  id: string;
  name: string;
  description?: string;
}

export const CreateUser: React.FC = () => {
  const { list } = useNavigation();
  const { formProps, saveButtonProps } = useForm<User>({
    resource: "users",
    meta: {
      gqlMutation: CREATE_AND_INVITE_USER_MUTATION,
    },
  });

  const [teams, setTeams] = useState<AuthTeam[]>([]);
  const [appTeams, setAppTeams] = useState<AppTeam[]>([]);
  const [loadingTeams, setLoadingTeams] = useState(true);
  const [loadingAppTeams, setLoadingAppTeams] = useState(true);
  const [isCreating, setIsCreating] = useState(false);

  // Fetch teams from auth server
  useEffect(() => {
    const fetchTeams = async () => {
      try {
        setLoadingTeams(true);
        const result = await gqlClient.query(GET_AUTH_TEAMS_QUERY, {}).toPromise();
        if (result.data && result.data.getAuthTeams) {
          setTeams(result.data.getAuthTeams);
        }
      } catch (error) {
        console.error("Failed to fetch teams:", error);
        message.error("Failed to load teams");
      } finally {
        setLoadingTeams(false);
      }
    };

    fetchTeams();
  }, []);

  // Fetch app teams
  useEffect(() => {
    const fetchAppTeams = async () => {
      try {
        setLoadingAppTeams(true);
        const result = await gqlClient.query(GET_APP_TEAMS_QUERY, {}).toPromise();
        if (result.data && result.data.getAppTeams) {
          setAppTeams(result.data.getAppTeams);
        }
      } catch (error) {
        console.error("Failed to fetch app teams:", error);
        message.error("Failed to load app teams");
      } finally {
        setLoadingAppTeams(false);
      }
    };

    fetchAppTeams();
  }, []);

  const handleCreateAndInvite = async () => {
    try {
      setIsCreating(true);
      const values = await formProps.form?.validateFields();
      
      console.log('Form values:', values);
      
      if (!values) {
        message.error("Please fill in all required fields");
        return;
      }

      // Type assertion for form values
      const formValues = values as {
        email: string;
        teamId?: string;
        organizationalRole?: string;
        appTeams?: string[];
      };

      // Check if teamId is missing
      if (!formValues.teamId) {
        console.error("Team ID is missing from form values");
        message.error("Please select a team");
        return;
      }

     const result = await gqlClient.mutation(CREATE_AND_INVITE_USER_MUTATION, {
       input: {
         email: formValues.email,
         teamId: formValues.teamId,
         organizationalRole: formValues.organizationalRole,
         appTeams: formValues.appTeams,
       }
     }).toPromise();

     if (result.error) {
       // Check if the error indicates the user is already a member
       const errorMessage = result.error.message || '';
        if (errorMessage.includes('USER_IS_ALREADY_A_MEMBER_OF_THIS_ORGANIZATION') ||
            errorMessage.includes('already exists and is active')) {
          message.success("User was already invited");
          list("users");
          return;
        }
       throw result.error;
     }

      message.success("User created and invitation sent successfully!");
      list("users");
    } catch (error) {
      console.error("Failed to create and invite user:", error);
      message.error("Failed to create and invite user: " + (error instanceof Error ? error.message : "Unknown error"));
   } finally {
     setIsCreating(false);
   }
  };

  return (
    <Create saveButtonProps={saveButtonProps} title="Create User">
      <Form layout="vertical" {...formProps}>
        <Form.Item
          label="Email"
          name="email"
          rules={[{ required: true, message: "Please enter user email" }, { type: "email", message: "Please enter a valid email" }]}
        >
          <Input placeholder="Enter user email" />
        </Form.Item>

        <Form.Item
          label="Team"
          name="teamId"
          rules={[{ required: true, message: "Please select a team" }]}
        >
          <Spin spinning={loadingTeams}>
            <Select
              placeholder="Select team"
              disabled={loadingTeams}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.children?.toString().toLowerCase() || '').includes(input.toLowerCase())
              }
              onChange={(value) => {
                console.log('Team selected:', value);
                formProps.form?.setFieldsValue({ teamId: value });
              }}
            >
              {teams.map((team) => (
                <Option key={team.id} value={team.id}>
                  {team.name} {team.description && `(${team.description})`}
                </Option>
              ))}
            </Select>
          </Spin>
        </Form.Item>

        <Form.Item label="App Teams" name="appTeams">
          <Spin spinning={loadingAppTeams}>
            <Select
              mode="multiple"
              placeholder="Select app teams (optional)"
              disabled={loadingAppTeams}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.children?.toString().toLowerCase() || '').includes(input.toLowerCase())
              }
            >
              {appTeams.map((team) => (
                <Option key={team.id} value={team.id}>
                  {team.name} {team.description && `(${team.description})`}
                </Option>
              ))}
            </Select>
          </Spin>
        </Form.Item>

        <Form.Item label="Organization Role" name="organizationalRole">
          <Input placeholder="Enter organization role (optional)" />
        </Form.Item>

        <Space style={{ marginTop: 16 }}>
          <Button
            type="primary"
            onClick={handleCreateAndInvite}
            loading={isCreating}
          >
            Create & Invite
          </Button>
        </Space>
      </Form>
    </Create>
  );
};